import { useCallback, useEffect, useState } from "react";

import { PageContainer } from "../../components/PageComponents";
import { Transable } from "./TransactionTable";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../redux/store";
import {
  fetchTransactions,
  setFilters as setTransactionFilters,
  clearFilters as clearTransactionFilters,
} from "../../features/auth/store/transactionSlice";
import {
  TransactionFilters,
  TransactionPagination,
} from "../../features/auth/store/types/transaction-types";
import { SearchIcon, Dropdown } from "../../assets/Icons";

const Transactions = () => {
  const dispatch = useAppDispatch();
  const { transactions, pagination, loading, error, filters } = useAppSelector(
    (state: RootState) => state.transactions,
  );

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchData = useCallback(() => {
    const params: Partial<TransactionFilters> = { ...filters };

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    } else {
      delete params.search;
    }

    dispatch(fetchTransactions(params));
  }, [dispatch, filters, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (
    key: keyof TransactionFilters,
    value: string | number,
  ) => {
    dispatch(
      setTransactionFilters({
        [key]: value,
        ...(key !== "page" ? { page: 1 } : {}),
      } as Partial<TransactionFilters>),
    );
  };

  const handleClearFilters = () => {
    dispatch(clearTransactionFilters());
  };

  return (
    <PageContainer title="Transactions">
      <div className="flex flex-col gap-4">
        <TransactionSearchFilter
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          loading={loading}
        />
        <div className="border border-[#FFFFFF1A] rounded-2xl">
          {error && (
            <div className="text-red-500 text-sm px-4 py-2 border-b border-[#FFFFFF1A]">
              {error}
            </div>
          )}
          <Transable transactions={transactions} loading={loading} />
          {pagination && (
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={(page) => handleFilterChange("page", page)}
            />
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Transactions;

type TransactionSearchFilterProps = {
  filters: TransactionFilters;
  onFilterChange: (
    key: keyof TransactionFilters,
    value: string | number,
  ) => void;
  onClearFilters: () => void;
  loading: boolean;
};

const TransactionSearchFilter = ({
  filters,
  onFilterChange,
  onClearFilters,
  loading,
}: TransactionSearchFilterProps) => {
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "0", label: "Successful" },
    { value: "1", label: "Pending" },
    { value: "2", label: "Paid" },
    { value: "3", label: "Queued" },
    { value: "6", label: "Failed" },
    { value: "5", label: "Reversed" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "1", label: "Airtime" },
    { value: "2", label: "Data" },
    { value: "3", label: "Cable" },
    { value: "4", label: "Electricity" },
    { value: "5", label: "Top-up" },
    { value: "6", label: "Game Card" },
  ];

  const handleDateChange = (key: "start_date" | "end_date", value: string) => {
    onFilterChange(key, value);
  };

  return (
    <div className="flex flex-col bp:flex-row bp:items-center bp:justify-between mb-4 gap-2">
      <div className="relative w-full bp:w-auto">
        <input
          type="text"
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          placeholder="Search by reference or description..."
          disabled={loading}
          className="border p-2 pl-10 rounded-3xl w-full bp:w-[500px] h-[40px] bp:h-[48px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2 w-full bp:w-auto">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => handleDateChange("start_date", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[130px] h-[40px] rounded-3xl disabled:opacity-50 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => handleDateChange("end_date", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[130px] h-[40px] rounded-3xl disabled:opacity-50 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
            placeholder="End Date"
          />
        </div>
        <div className="relative">
          <select
            value={filters.status}
            onChange={(e) => onFilterChange("status", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[200px] h-[40px] rounded-3xl appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          >
            {statusOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className="bg-white dark:bg-[#27272A] text-black dark:text-white"
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Dropdown />
          </div>
        </div>
        <div className="relative">
          <select
            value={filters.type}
            onChange={(e) => onFilterChange("type", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[200px] h-[40px] rounded-3xl appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          >
            {typeOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className="bg-white dark:bg-[#27272A] text-black dark:text-white"
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Dropdown />
          </div>
        </div>
        <button
          onClick={onClearFilters}
          disabled={loading}
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-3xl text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33] disabled:opacity-50"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

type PaginationProps = {
  pagination: TransactionPagination;
  loading: boolean;
  onPageChange: (page: number) => void;
};

const Pagination = ({ pagination, loading, onPageChange }: PaginationProps) => {
  const { current_page, last_page, from, to, total } = pagination;

  const handlePrevious = () => {
    if (current_page > 1) {
      onPageChange(current_page - 1);
    }
  };

  const handleNext = () => {
    if (current_page < last_page) {
      onPageChange(current_page + 1);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center border-t border-[#FFFFFF1A] p-4 gap-2">
      <div className="flex gap-2">
        <button
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
          onClick={handlePrevious}
          disabled={current_page <= 1 || loading}
        >
          Previous
        </button>
        <button
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
          onClick={handleNext}
          disabled={current_page >= last_page || loading}
        >
          Next
        </button>
      </div>
      <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
        Showing {from || 0} to {to || 0} of {total || 0} results
        {last_page > 1 && ` (Page ${current_page} of ${last_page})`}
      </span>
    </div>
  );
};
